var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * React Query hooks for proposal contacts
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProposalContacts, createProposalContact, updateProposalContact, deleteProposalContact } from '../services/ProposalContactsService';
/**
 * Hook for fetching proposal contacts
 * @param proposalId - The proposal ID
 * @returns Query result with contacts data
 */
export function useProposalContacts(proposalId) {
    return useQuery(['proposalContacts', proposalId], () => getProposalContacts(proposalId), {
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        enabled: !!proposalId
    });
}
/**
 * Hook for creating a new proposal contact
 * @param proposalId - The proposal ID
 * @returns Mutation for creating contacts
 */
export function useCreateProposalContact(proposalId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (contactData) => createProposalContact(proposalId, contactData),
        onSuccess: () => {
            // Invalidate and refetch contacts
            void queryClient.invalidateQueries(['proposalContacts', proposalId]);
        },
        onError: (error) => {
            console.error('Error creating contact:', error);
        }
    });
}
/**
 * Hook for updating a proposal contact
 * @param proposalId - The proposal ID
 * @returns Mutation for updating contacts
 */
export function useUpdateProposalContact(proposalId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ contactId, contactData }) => updateProposalContact(proposalId, contactId, contactData),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ contactId, contactData }) {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries(['proposalContacts', proposalId]);
            // Snapshot the previous value
            const previousContacts = queryClient.getQueryData(['proposalContacts', proposalId]);
            // Optimistically update to the new value
            if (previousContacts) {
                const contactExists = previousContacts.some((c) => c.id === contactId);
                if (!contactExists) {
                    console.warn(`Contact ${contactId} not found for optimistic update`);
                    return { previousContacts };
                }
                queryClient.setQueryData(['proposalContacts', proposalId], (old) => (old === null || old === void 0 ? void 0 : old.map((contact) => (contact.id === contactId ? Object.assign(Object.assign({}, contact), contactData) : contact))) || []);
            }
            // Return a context object with the snapshotted value
            return { previousContacts };
        }),
        onError: (err, variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousContacts) {
                queryClient.setQueryData(['proposalContacts', proposalId], context.previousContacts);
            }
            console.error('Error updating contact:', err);
        },
        onSettled: () => {
            // Always refetch after error or success
            void queryClient.invalidateQueries(['proposalContacts', proposalId]);
        }
    });
}
/**
 * Hook for deleting a proposal contact
 * @param proposalId - The proposal ID
 * @returns Mutation for deleting contacts
 */
export function useDeleteProposalContact(proposalId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (contactId) => deleteProposalContact(proposalId, contactId),
        onMutate: (contactId) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries(['proposalContacts', proposalId]);
            // Snapshot the previous value
            const previousContacts = queryClient.getQueryData(['proposalContacts', proposalId]);
            // Optimistically update to the new value
            if (previousContacts) {
                queryClient.setQueryData(['proposalContacts', proposalId], (old) => (old === null || old === void 0 ? void 0 : old.filter((contact) => contact.id !== contactId)) || []);
            }
            // Return a context object with the snapshotted value
            return { previousContacts };
        }),
        onError: (err, variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousContacts) {
                queryClient.setQueryData(['proposalContacts', proposalId], context.previousContacts);
            }
            console.error('Error deleting contact:', err);
        },
        onSettled: () => {
            // Always refetch after error or success
            void queryClient.invalidateQueries(['proposalContacts', proposalId]);
        }
    });
}
